import { useEffect, useRef, useState } from "react";
import type { InvitationConfig, InvitationPhoto } from "../../types/invitation";
import {
  MAX_INVITATION_PHOTOS,
  uploadInvitationPhoto,
  validateInvitationPhoto,
} from "../../lib/invitationPhotos";

export default function InvitationPhotos({
  value,
  onChange,
  onBusyChange,
}: {
  value: InvitationConfig;
  onChange: (patch: Partial<InvitationConfig>) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const mounted = useRef(true);
  const inFlight = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const photos = value.photos || [];
  const upload = async (input: HTMLInputElement, featured: boolean) => {
    const files = Array.from(input.files || []);
    input.value = "";
    if (!files.length || inFlight.current) return;
    setError("");
    setStatus("");
    if (!featured && files.length + photos.length > MAX_INVITATION_PHOTOS) {
      setError(
        `You can add up to ${MAX_INVITATION_PHOTOS} gallery photos. Choose fewer photos or remove one first.`,
      );
      return;
    }
    try {
      files.forEach(validateInvitationPhoto);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Check your photos and try again.",
      );
      return;
    }
    inFlight.current = true;
    setBusy(true);
    onBusyChange(true);
    const uploaded: InvitationPhoto[] = [];
    try {
      for (const file of files) {
        if (!mounted.current) break;
        setStatus(`Preparing photo ${uploaded.length + 1} of ${files.length}…`);
        const photo = await uploadInvitationPhoto(file);
        uploaded.push(photo);
        if (mounted.current)
          onChange(
            featured
              ? {
                  featuredPhoto: {
                    ...photo,
                    caption: value.featuredPhoto?.caption || "",
                  },
                }
              : { photos: [...photos, ...uploaded] },
          );
      }
      if (mounted.current)
        setStatus(
          "Photos ready. Save your invitation to share them with guests.",
        );
    } catch (error) {
      if (mounted.current) {
        setStatus(
          uploaded.length
            ? `${uploaded.length} photo(s) added to your draft. Save to keep them.`
            : "",
        );
        setError(
          error instanceof Error
            ? error.message
            : "Unable to upload. Please try again.",
        );
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setBusy(false);
        onBusyChange(false);
      }
    }
  };
  const caption = (photo: InvitationPhoto, index?: number) => (
    <label className="block text-sm mt-2">
      {index === undefined
        ? "Featured photo caption"
        : `Photo ${index + 1} caption`}
      <input
        className="block w-full border rounded p-2 mt-1"
        maxLength={160}
        placeholder="A short caption (optional)"
        value={photo.caption}
        onChange={(e) =>
          onChange(
            index === undefined
              ? { featuredPhoto: { ...photo, caption: e.target.value } }
              : {
                  photos: photos.map((p, i) =>
                    i === index ? { ...p, caption: e.target.value } : p,
                  ),
                },
          )
        }
      />
    </label>
  );
  const move = (index: number, offset: number) => {
    const next = [...photos];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    onChange({ photos: next });
  };
  return (
    <section
      aria-label="Invitation photos"
      className="border rounded-xl p-4 space-y-4 bg-rose-50/40"
    >
      <div>
        <h3 className="font-medium">Your photos</h3>
        <p className="text-sm text-gray-600">
          Add photos of the couple, the guest of honour, family or your
          celebration. Photos are visible on your shared invitation.
        </p>
        <p className="text-xs text-gray-500 mt-1">
          JPG, PNG or WebP · up to 10 MB each. Photos are resized for faster
          loading.
        </p>
      </div>
      <div>
        <h4 className="font-medium text-sm">Featured photo</h4>
        <p className="text-xs text-gray-500 mb-2">
          {value.template !== "above-the-clouds"
            ? "Shown in the opening section beside your names. Without a photo, an elegant monogram takes its place."
            : "Shown beside your invitation message. The cloud entrance stays in place."}
        </p>
        {value.featuredPhoto && (
          <div className="mb-3">
            <img
              src={value.featuredPhoto.url}
              alt="Featured photo preview"
              className="h-48 w-full object-contain rounded bg-white"
            />
            {caption(value.featuredPhoto)}
            <button
              type="button"
              className="text-sm text-rose-700 mt-2"
              onClick={() => onChange({ featuredPhoto: undefined })}
            >
              Remove featured photo
            </button>
          </div>
        )}
        <label className="block text-sm">
          {value.featuredPhoto
            ? "Replace featured photo"
            : "Upload featured photo"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={busy}
            className="block w-full text-sm mt-2"
            onChange={(e) => void upload(e.currentTarget, true)}
          />
        </label>
      </div>
      <div>
        <h4 className="font-medium text-sm">
          Photo gallery ({photos.length}/{MAX_INVITATION_PHOTOS})
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          A collection of memories before the function timeline.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {photos.map((photo, index) => (
            <div
              className="rounded-lg border p-3 bg-white min-w-0"
              key={`${photo.id}-${index}`}
            >
              <img
                src={photo.url}
                alt={`Gallery photo ${index + 1} preview`}
                className="h-40 w-full object-contain rounded"
              />
              {caption(photo, index)}
              <div className="flex flex-wrap gap-3 text-sm mt-2">
                <button
                  type="button"
                  aria-label={`Move photo ${index + 1} earlier`}
                  disabled={index === 0}
                  className="disabled:opacity-40"
                  onClick={() => move(index, -1)}
                >
                  ← Earlier
                </button>
                <button
                  type="button"
                  aria-label={`Move photo ${index + 1} later`}
                  disabled={index === photos.length - 1}
                  className="disabled:opacity-40"
                  onClick={() => move(index, 1)}
                >
                  Later →
                </button>
                <button
                  type="button"
                  aria-label={`Remove photo ${index + 1}`}
                  className="text-rose-700"
                  onClick={() =>
                    onChange({ photos: photos.filter((_, i) => i !== index) })
                  }
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <label className="block text-sm mt-3">
          Add gallery photos
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            disabled={busy || photos.length >= MAX_INVITATION_PHOTOS}
            className="block w-full text-sm mt-2"
            onChange={(e) => void upload(e.currentTarget, false)}
          />
        </label>
      </div>
      {status && (
        <p role="status" className="text-sm">
          {status}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
